import {Component, EventEmitter, Input, Output} from '@angular/core';
import { getLatestBV } from 'app/util/borrowerValues/getLatest';
import { BorrowerValue } from 'app/models/dto/borrower-value.model';
import {getFormattedQuarterYear} from 'ng2-app/util/borrowerValues/getQuarterYearStartDateAlias';
import { BorrowerValueService } from 'ng2-app/services/borrower-value/borrower-value.service';

@Component({
  selector: 'app-erc-data-table',
  templateUrl: './erc-data-table.component.html',
  styleUrls: ['./erc-data-table.component.scss']
})

export class ErcDataTableComponent {
  @Input() title: string;
  @Input() loading: boolean;
  @Input() set borrowerValues(borrowerValues: BorrowerValue[] | undefined) {
    if (borrowerValues && borrowerValues.length > 0) {
      this._borrowerValues = borrowerValues;
      if (this.ercGrantAndCreditInfo.length !== this.grantsCredits.length) {
        for (const alias of this.ercGrantAndCreditInfo) {
          this.grantsCredits.push(getLatestBV(borrowerValues, alias));
        }
      }

      this.grantsCreditsReceived = this.reduceGrants(this.grantsCredits);
      if (Object.keys(this.grantsCreditsReceived).length > 1) {
        for (const key of Object.keys(this.grantsCreditsReceived)) {
          if (this.grantsCreditsReceived[key][0].alias.includes('amount') && this.grantsCreditsReceived[key][0].value === null) {
            delete this.grantsCreditsReceived[key];
          }
        }
      }
    }
    this.updateFields();
  }
  get borrowerValues() {
    return this._borrowerValues;
  }
  @Input() businessStartDate: string | null;
  @Output() editClicked = new EventEmitter();

  grantsCredits: any[] = [];
  grantsCreditsReceived: any;
  fields: {label: string | object, value: string | object}[];
  businessInfoDefault = [
    'borrowerStreet',
    'borrowerCity',
    'borrowerState',
    'borrowerZip',
    'separate_mailing_address',
    'industry_naics',
    'average_employees_2019'
  ];
  businessInfoSeparateMailing = [
    'borrowerStreet',
    'borrowerCity',
    'borrowerState',
    'borrowerZip',
    'separate_mailing_address',
    'borrowerMailingStreet',
    'borrowerMailingCity',
    'borrowerMailingState',
    'borrowerMailingZip',
    'industry_naics',
    'average_employees_2019'
  ];
  fieldsMap = {
    'Qualifications': {
      fields: [
        'has_w2_employees',
        'impacted_by_govt_order',
        'revenue_decreased_2020_2021',
        'operation_start_date',
        'average_employees_2020',
        'average_employees_2021',
      ]
    },
    'Basic Info': {
      fields: [
        'uses_peo',
        {
          label: 'Grants & Credits Received',
          value: () => !!this.grantsCreditsReceived ? this.getReceivedGrantsCreditsAsString() : ''
        },
        'received_ppp_loan',
        'business_taxes_form'
      ]
    },
    'Ownership Info': {
      fields: [
        'contactsOwnOtherBusinesses',
        'hasRelatedOwners',
        'spouseOwnsOtherBusinesses',
        'hasRelatedW2Employees'
      ]
    },
    'Business Info': {
      fields:<any> []
    },
    'Gross Receipts': {
      fields: [
        'quarterlyRevenue2019Q1',
        'quarterlyRevenue2019Q2',
        'quarterlyRevenue2019Q3',
        'quarterlyRevenue2019Q4',
        'quarterlyRevenue2020Q1',
        'quarterlyRevenue2020Q2',
        'quarterlyRevenue2020Q3',
        'quarterlyRevenue2020Q4',
        'quarterlyRevenue2021Q1',
        'quarterlyRevenue2021Q2',
        'quarterlyRevenue2021Q3',
        'quarterlyRevenue2021Q4',
      ]
    },
    'Old Government Order Info': {
      fields: [
        'impacted_by_govt_order',
        'gov_order_time_frame',
        'gov_order_directed',
        'govt_order_info',
        'govOrderImpactDocumentation',
      ]
    },
    'Government Order Info': {
      fields: [
        'impacted_by_govt_order',
        'how_revenue_is_generated',
        'business_operation_locations',
        'customer_locations',
      ],
      conditionalFields: [
        {
          constantFields: ['completely_shut_down'],
          condition: () => {
            return this.getBorrowerValue('completely_shut_down') === 'Yes';
          },
          fields: [
            'shut_down_start',
            'shut_down_end',
            'mods_on_reopen',
            'gov_order_time_frame',
          ]
        },
        {
          constantFields: ['prevented_customer_access'],
          condition: () => {
            return this.getBorrowerValue('prevented_customer_access') === 'Yes';
          },
          fields: [
            'customer_visit_affected_period',
            'customer_visits_comparable_period',
            'customer_visits_explanation',
          ]
        },
        {
          constantFields: ['reduction_in_customers_served'],
          condition: () => {
            return this.getBorrowerValue('reduction_in_customers_served') === 'Yes';
          },
          fields: [

            'customers_served_affected_period',
            'customers_served_comparable_period',
            'customers_served_explanation',
          ]
        },
        {
          constantFields: ['reduction_in_contracts'],
          condition: () => {
            return this.getBorrowerValue('reduction_in_contracts') === 'Yes';
          },
          fields: [
            'contracts_affected_period',
            'contracts_comparable_period_',
            'contracts_explanation',
          ]
        },
        {
          constantFields: ['projects_affected'],
          condition: () => {
            return this.getBorrowerValue('projects_affected') === 'Yes';
          },
          fields: [
            'projects_affected_period',
            'projects_comparable_period',
            'projects_affected_explanation',
          ]
        },
        {
          constantFields: ['production_declined'],
          condition: () => {
            return this.getBorrowerValue('production_declined') === 'Yes';
          },
          fields: [
            'production_affected_period',
            'production_comparable_period',
            'production_explanation',
          ]
        },
        {
          constantFields: ['sales_reduced'],
          condition: () => {
            return this.getBorrowerValue('sales_reduced') === 'Yes';
          },
          fields: [
            'sales_reduced_affected_period',
            'sales_reduced_comparable_period',
            'sales_reduced_explanation',
          ]
        },
        {
          constantFields: ['reduced_hours'],
          condition: () => {
            return this.getBorrowerValue('reduced_hours') === 'Yes';
          },
          fields: [
            'operation_hours_affected_period',
            'operation_hours_comparable_period',
            'reduced_hours_explanation',
          ]
        },
        {
          constantFields: ['staggered_shifts'],
          condition: () => {
            return this.getBorrowerValue('staggered_shifts') === 'Yes';
          },
          fields: [
            'staggered_shifts_description',
            'staggered_shifts_explanation',
          ]
        },
        {
          constantFields: ['workspace_modifications'],
          condition: () => {
            return this.getBorrowerValue('workspace_modifications') === 'Yes';
          },
          fields: [
            'workspace_modifications_description',
          ]
        },
        {
          constantFields: ['capacity_limitation'],
          condition: () => {
            return this.getBorrowerValue('capacity_limitation') === 'Yes';
          },
          fields: [
            'capacity_affected_period',
            'capacity_comparable_period',
            'capacity_limitation_explanation',
          ]
        },
        {
          constantFields: ['in-person_meetings_affected'],
          condition: () => {
            return this.getBorrowerValue('in-person_meetings_affected') === 'Yes';
          },
          fields: [
            'meetings_affected_description',
            'meeting_affected_explanation',
          ]
        },
        {
          constantFields: ['suppliers_shutdown'],
          condition: () => {
            return this.getBorrowerValue('suppliers_shutdown') === 'Yes';
          },
          fields: [
            'supplier_details',
            'supplier_shutdown_timeframe',
          ]
        },
      ],
    },
    'PEO Info': {
      fields: [
        'uses_peo',
        'peo_name',
        'peoFirstName',
        'peoLastName',
        'peoPhone',
        'peoEmail',
      ]
    },
    'PPP Info': {
      fields: [
        'received_ppp_loan',
        'ppp_loans',
        'ppp1LoanForgivenessType',
        'ppp1PayrollAmountClaimed',
        'ppp1CoverageStartDate',
        'ppp1CoverageEndDate',
        'ppp2LoanForgivenessType',
        'ppp2PayrollAmountClaimed',
        'ppp2CoverageStartDate',
        'ppp2CoverageEndDate',
      ]
    }
  };

  private _borrowerValues: BorrowerValue[];
  private ercGrantAndCreditInfo = [
    'shutter_grant_amount',
    'shutter_grant_coverage_start',
    'shutter_grant_coverage_end',
    'restaurant_grant_amount',
    'restaurant_grant_coverage_start',
    'restaurant_grant_coverage_end',
    'research_credit_amount',
    'research_credit_coverage_start',
    'research_credit_coverage_end',
    'indian_credit_amount',
    'indian_credit_coverage_start',
    'indian_credit_coverage_end',
    'fmla_credit_amount',
    'fmla_credit_coverage_start',
    'fmla_credit_coverage_end',
    'ffcra_credit_amount',
    'ffcra_credit_coverage_start',
    'ffcra_credit_coverage_end',
    'empowerment_credit_amount',
    'empowerment_credit_coverage_start',
    'empowerment_credit_coverage_end',
    'paid_leave_credit_amount',
    'paid_leave_credit_coverage_start',
    'paid_leave_credit_coverage_end',
    'work_opportunity_credit_amount',
    'work_opportunity_credit_coverage_start',
    'work_opportunity_credit_coverage_end',
  ];

  constructor(
    private BVService: BorrowerValueService
  ) {
  }

  updateFields(): void {

    this.fieldsMap['Business Info'].fields = this.getBorrowerValue('separate_mailing_address') === 'Yes'
      ? this.businessInfoSeparateMailing
      : this.businessInfoDefault;

    const fieldGroupConfig = this.fieldsMap[this.title];
    if(fieldGroupConfig) {
      let currentFields = fieldGroupConfig.fields;
      if(fieldGroupConfig.conditionalFields) {
        fieldGroupConfig.conditionalFields.forEach( cField => {
          currentFields = [
            ...currentFields,
            ...cField.constantFields,
          ];
          if(cField.condition()) {
            currentFields = [
              ...currentFields,
              ...cField.fields,
            ];
          }
        });
      }

      this.fields = currentFields.map( field => this.getFieldConfig(field));
    }
  }

  getFieldConfig(field) {
    return typeof field === 'string' ?
      {
        label: this.getBorrowerValueShortName(field),
        value: this.getBorrowerValue(field),
      } :
      {
        label: typeof field.label === 'function' ? field.label() : field.label,
        value: typeof field.value === 'function' ? field.value() : field.value,
      };
  }


  getObjectKeys(object) {
    return Object.keys(object);
  }

  getBorrowerValue(alias) {
    const borrowerValue = getLatestBV(this.borrowerValues, alias);

    if (this.businessStartDate && this.title === 'Gross Receipts') {
      const formattedDate = getFormattedQuarterYear(this.businessStartDate);
      if (borrowerValue?.alias && formattedDate && formattedDate > borrowerValue?.alias) {
        return 'N/A';
      }
    }

    if(borrowerValue) {
      if(borrowerValue.type === 'naics') {
        return borrowerValue.value;
      }
      return this.BVService.borrowerValueToString(borrowerValue);
    }

    return '-';
  }

  getBorrowerValueShortName(alias) {
    return getLatestBV(this.borrowerValues, alias)?.shortName;
  }

  getReceivedGrantsCreditsAsString() {
    let grantCreditNames = '';
    Object.keys(this.grantsCreditsReceived).forEach(grantCreditName => {
      if (!grantCreditNames.includes(grantCreditName)) {
        grantCreditNames += grantCreditName + ', ';
      }
    });
    return grantCreditNames.length > 0 ? grantCreditNames.replace(/,\s*$/, '') : '-';
  }

  reduceGrants(grantsCredits) {
     return grantsCredits.reduce((store, bv) => {
      const grouping = bv.shortName.includes('Grant') ?
        bv.shortName.split('Grant ')[0] + 'Grant' :
        bv.shortName.split('Credit ')[0] + 'Credit';

      const keyStore = (store[grouping] || (store[grouping] = []));
      keyStore.push(bv);

      return store;
    }, {});
  }

  onEditClicked() {
    this.editClicked.emit();
  }
}
